<script setup>
import {computed, ref, watch} from 'vue';
import {DateTime} from 'luxon';

// Optional playing-area picker for the fixture schedule form. Renders nothing
// when the club has no areas configured (that IS the feature gating), and shows
// a non-blocking warning when other fixtures sit on the chosen area near the
// chosen time.
const props = defineProps({
  clubId: {type: String, default: null},
  sportId: {type: String, default: null},
  excludeFixtureId: {type: String, default: null},
  date: {type: String, default: null},
  time: {type: String, default: null},
});
const modelValue = defineModel({type: String, default: null});

const {outputDateFromDb} = useCrudConverters();

const {items: areas, getItems: getAreas} = useCrudApi('api/v2/playingAreas');
const {items: conflictFixtures, getItems: getConflictFixtures} = useCrudApi('api/v2/fixtures');

const loadAreas = async () => {
  if (!props.clubId) return;
  const filters = {clubIds: [props.clubId]};
  if (props.sportId) filters.sportIds = [props.sportId];
  await getAreas(1, 100, filters);
};
// Re-fetch on sportId too: it can arrive a tick after clubId when the dialog
// loads, and an unfiltered fetch would show other sports' areas.
watch(() => [props.clubId, props.sportId], loadAreas, {immediate: true});

const areaOptions = computed(() =>
    (areas.value || []).map(a => ({value: a.id, title: a.name})));

// Conflict warning: other fixtures on this area within ±2 hours of the chosen time
const conflicts = ref([]);
watch([modelValue, () => props.date, () => props.time], async ([areaId, date, time]) => {
  conflicts.value = [];
  if (!areaId || !date || !time) return;
  // Same local-zone parse then UTC ISO as inputDateForDb, with a ±2h window
  const centre = DateTime.fromFormat(date + ' ' + time, 'yyyy-MM-dd HH:mm');
  if (!centre.isValid) return;
  const start = centre.minus({hours: 2}).toUTC().toISO();
  const end = centre.plus({hours: 2}).toUTC().toISO();
  await getConflictFixtures(1, 5, {
    playingAreaIds: [areaId],
    scheduledAfter: start,
    scheduledBefore: end,
  });
  conflicts.value = (conflictFixtures.value || [])
      .filter(f => f.id !== props.excludeFixtureId);
});

const conflictLine = (f) => {
  const when = f.scheduledAt || f.homeTeamScheduledAt || f.awayTeamScheduledAt;
  const time = when ? outputDateFromDb(when, 'd MMM HH:mm') : '';
  const home = f.homeTeam?.name || 'TBC';
  const away = f.awayTeam?.name || 'TBC';
  return `${home} vs ${away}${time ? ' at ' + time : ''}`;
};
</script>

<template>
  <div v-if="areaOptions.length" class="mt-3">
    <div class="tl-label mb-2">Playing area (optional)</div>
    <v-select
        v-model="modelValue"
        :items="areaOptions"
        item-value="value"
        item-title="title"
        clearable
        placeholder="Not decided yet"
    />
    <v-alert
        v-if="conflicts.length"
        type="warning"
        variant="tonal"
        density="compact"
        class="mt-2"
    >
      <div class="text-body-2 font-weight-bold">Already booked around this time</div>
      <div v-for="(conflict, index) in conflicts" :key="index" class="text-caption">
        {{ conflictLine(conflict) }}
      </div>
    </v-alert>
  </div>
</template>
